import { useEffect, useState } from 'react'
import { isDesktop, urlDeDownload } from '../lib/platform.ts'
import { assinaturaNoServidor, assinaturaRodando } from './update-check.ts'

/**
 * Secao "Sobre" das Preferencias (pedido 2026-08-25): onde se VE a versao e se
 * CONFERE se ha atualizacao, sem depender do toast automatico.
 *
 * Duas versoes convivem:
 * - a do APLICATIVO (o bundle web) — atualiza a cada deploy; "atualizar" e
 *   recarregar a janela;
 * - a do SHELL desktop (instalador) — so muda quando o nativo muda; atualizar
 *   exige baixar o instalador novo em /download.
 */

/** Carimbo definido no vite.config.ts na hora do build. */
declare const __BUILD_EM__: string

/** Hash curto do bundle rodando — identifica o deploy sem inventar numero. */
function versaoDoBundle(): string {
  const assinatura = assinaturaRodando()
  if (!assinatura) return 'dev'
  return assinatura.replace('/assets/index-', '').replace('.js', '')
}

async function versaoDoShell(): Promise<string | null> {
  if (!isDesktop()) return null
  try {
    const w = window as unknown as {
      __TAURI_INTERNALS__?: { invoke?: (cmd: string) => Promise<unknown> }
    }
    return ((await w.__TAURI_INTERNALS__?.invoke?.('plugin:app|version')) as string) ?? null
  } catch {
    return null
  }
}

function versaoMaisNova(a: string, b: string): boolean {
  const pa = a.split('.').map(Number)
  const pb = b.split('.').map(Number)
  for (let i = 0; i < 3; i++) {
    if ((pa[i] ?? 0) > (pb[i] ?? 0)) return true
    if ((pa[i] ?? 0) < (pb[i] ?? 0)) return false
  }
  return false
}

type Checagem =
  | { estado: 'parado' }
  | { estado: 'checando' }
  | { estado: 'atualizado' }
  | { estado: 'app-nova' }
  | { estado: 'shell-nova'; versao: string }
  | { estado: 'indisponivel' }

export function SobreSecao() {
  const [shell, setShell] = useState<string | null>(null)
  const [checagem, setChecagem] = useState<Checagem>({ estado: 'parado' })
  const noDesktop = isDesktop()

  useEffect(() => {
    void versaoDoShell().then(setShell)
  }, [])

  const buildEm = new Date(__BUILD_EM__)
  const buildLegivel = Number.isNaN(buildEm.getTime())
    ? __BUILD_EM__
    : buildEm.toLocaleString('pt-BR', { dateStyle: 'short', timeStyle: 'short' })

  const verificar = async () => {
    setChecagem({ estado: 'checando' })

    // 1. o bundle web mudou no servidor? (em dev nao ha hash — pula direto)
    const rodando = assinaturaRodando()
    if (rodando !== null) {
      const noServidor = await assinaturaNoServidor()
      if (noServidor === null) {
        setChecagem({ estado: 'indisponivel' })
        return
      }
      if (noServidor !== rodando) {
        setChecagem({ estado: 'app-nova' })
        return
      }
    }

    // 2. no desktop, o instalador do shell tambem pode ter versao nova
    if (noDesktop && shell) {
      try {
        const res = await fetch('/download/releases')
        const dados = (await res.json()) as {
          releases: { platform: string; version: string }[]
        }
        const ultima = dados.releases.find((r) => r.platform === 'windows')
        if (ultima && versaoMaisNova(ultima.version, shell)) {
          setChecagem({ estado: 'shell-nova', versao: ultima.version })
          return
        }
      } catch {
        // sem lista de releases nao ha o que comparar; o bundle ja esta ok
      }
    }

    setChecagem({ estado: 'atualizado' })
  }

  return (
    <>
      <section>
        <h3 className="prefs__section">Sobre o Orbind</h3>
        <dl className="prefs__versoes">
          <div>
            <dt>Versão do aplicativo</dt>
            <dd>
              <code>{versaoDoBundle()}</code> · publicada em {buildLegivel}
            </dd>
          </div>
          <div>
            <dt>Orbind Desktop</dt>
            <dd>{noDesktop ? (shell ? `versão ${shell}` : 'versão indisponível') : 'você está usando pelo navegador'}</dd>
          </div>
        </dl>
      </section>

      <section>
        <h3 className="prefs__section">Atualizações</h3>
        <div className="prefs__atualiza">
          <button type="button" className="prefs__checar" onClick={() => void verificar()} disabled={checagem.estado === 'checando'}>
            <i className="fas fa-rotate" aria-hidden /> Verificar atualizações
          </button>

          {checagem.estado === 'checando' ? <p className="prefs__hint">Verificando…</p> : null}
          {checagem.estado === 'atualizado' ? (
            <p className="prefs__hint prefs__hint--ok">
              <i className="fas fa-check" aria-hidden /> Você está na versão mais recente.
            </p>
          ) : null}
          {checagem.estado === 'indisponivel' ? (
            <p className="prefs__hint">Não foi possível consultar o servidor agora — tente de novo em instantes.</p>
          ) : null}
          {checagem.estado === 'app-nova' ? (
            <p className="prefs__hint">
              Há uma versão nova do aplicativo.{' '}
              <button type="button" className="prefs__link" onClick={() => window.location.reload()}>
                Atualizar agora
              </button>
            </p>
          ) : null}
          {checagem.estado === 'shell-nova' ? (
            <p className="prefs__hint">
              Há um instalador novo do Orbind Desktop (versão {checagem.versao}).{' '}
              <a className="prefs__link" href={urlDeDownload()} target="_blank" rel="noreferrer">
                Baixar instalador
              </a>
            </p>
          ) : null}
        </div>
        <p className="prefs__hint">
          O aplicativo se atualiza sozinho a cada publicação{noDesktop ? '; o instalador só muda quando o programa nativo muda' : ''}.
        </p>
      </section>
    </>
  )
}
